import { type JSX, useEffect, useState, useRef } from 'react';
import { $getSelection, $isRangeSelection, LexicalEditor, $createTextNode, $getNodeByKey } from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND, $createLinkNode } from '@lexical/link';
import { Button, Popover, PopoverBody, Form, Input } from 'reactstrap';
import { useToggle } from '@hooks/useToggle';
import { getSelectedNode } from '../utils';
import { ToolbarIconButton } from './ToolbarIconButton';

const LINK_FORMAT_DROPDOWN_ID = 'link-formatting-dropdown';
const LINK_ATTRIBUTES = { target: '_blank', rel: 'noopener noreferrer' };

interface Props {
  editor: LexicalEditor;
  isLink: boolean;
}

/** Finds link node from selection (node itself or parent) */
const findLinkNode = (selection: ReturnType<typeof $getSelection>) => {
  if (!$isRangeSelection(selection)) {
    return null;
  }
  const node = getSelectedNode(selection);
  if ($isLinkNode(node)) {
    return node;
  }
  const parent = node.getParent();
  return $isLinkNode(parent) ? parent : null;
};

export const LinkFormatDropDown = ({ editor, isLink }: Props): JSX.Element => {
  const [popoverOpen, toggle] = useToggle(false);
  const [url, setUrl] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [pendingLinkKey, setPendingLinkKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    if (pendingLinkKey) {
      editor.update(() => {
        const node = $getNodeByKey(pendingLinkKey);
        if ($isLinkNode(node)) {
          node.select();
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        }
      });
      setPendingLinkKey(null);
    }
    toggle();
  };

  const saveLink = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }

      const existingLink = findLinkNode(selection);
      if (!selection.isCollapsed() || existingLink) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url, ...LINK_ATTRIBUTES });
      } else {
        const linkNode = $createLinkNode(url, LINK_ATTRIBUTES);
        linkNode.append($createTextNode(url));
        selection.insertNodes([linkNode]);
      }
      setPendingLinkKey(null);
    });
    toggle();
  };

  useEffect(() => {
    if (!popoverOpen) {
      return;
    }
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }

      const linkNode = findLinkNode(selection);

      if (!linkNode && !selection.isCollapsed()) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url: 'https://', ...LINK_ATTRIBUTES });
        const newLinkNode = findLinkNode($getSelection());
        setPendingLinkKey(newLinkNode?.getKey() ?? null);
        setUrl('');
      } else {
        setPendingLinkKey(null);
        setUrl(linkNode?.getURL() ?? '');
      }

      // Capture selected text
      setSelectedText(selection.getTextContent());
    });

    // Focus input after editor update cycle completes
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [popoverOpen, editor]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveLink();
  };

  return (
    <>
      <ToolbarIconButton
        id={LINK_FORMAT_DROPDOWN_ID}
        icon='fal fa-paperclip'
        onClick={toggle}
        isActive={isLink || popoverOpen}
        caret
      />
      <Popover
        isOpen={popoverOpen}
        toggle={handleClose}
        placement='bottom'
        target={LINK_FORMAT_DROPDOWN_ID}
        trigger='legacy'
        hideArrow
        delay={0}
      >
        <PopoverBody className='editor__toolbar__submenu p-3'>
          <Form onSubmit={handleSubmit}>
            {selectedText && (
              <small className='d-block mb-2 text-muted text-truncate' style={{ maxWidth: '200px' }}>
                Link text: <strong className='text-dark'>{selectedText}</strong>
              </small>
            )}
            <Input
              innerRef={inputRef}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder='Enter URL'
              className='mb-2'
            />
            <div className='d-flex justify-content-end'>
              <Button disabled={!url} color='link' className='text-decoration-none p-0' onClick={saveLink}>
                Save
              </Button>
            </div>
          </Form>
        </PopoverBody>
      </Popover>
    </>
  );
};
